import React from 'react';

import { Select } from '@aline/common';
import type { DialogProps } from '@mui/material';
import { Dialog, Typography, useTheme } from '@mui/material';
import type { TransitionProps as MuiTransitionProps } from '@mui/material/transitions';
import clsx from 'clsx';
import { animated, useSpring } from 'react-spring';
/* Direct rather than via `src/components`, which re-exports this file. */
import { SmallCloseIconButton } from 'src/components/generic/SmallCloseIconButton';
/* Direct: the `src/hooks` barrel imports `src/components`, which re-exports this file. */
import { useAlineMeasure } from 'src/hooks/dom-hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<StyleProps>()(
  (theme, { contentPaddingBottomPx, contentPaddingPx, contentPaddingTopPx }) => ({
    closeButton: {
      alignSelf: 'flex-start',
      marginLeft: 'auto',
    },
    content: {
      display: 'flex',
      flexDirection: 'column' as const,
      flexGrow: 1,
      flexWrap: 'nowrap' as const,
      justifyContent: 'space-between',
      overflowY: 'auto' as const,
      paddingBottom: contentPaddingBottomPx ?? contentPaddingPx,
      paddingLeft: contentPaddingPx,
      paddingRight: contentPaddingPx,
      paddingTop: contentPaddingTopPx ?? contentPaddingPx,
    },
    dialogPaper: {
      '&:focus, &:focus-visible': {
        outline: 'none',
      },
      borderRadius: theme.dialogStyles.borderRadius,
    },
    header: {
      alignItems: 'baseline',
      display: 'flex',
      flexDirection: 'row' as const,
      padding: theme.spacing(2, 2, 0, 2),
    },
    title: {
      margin: theme.spacing(1, 1, 0),
    },
  })
);

interface StyleProps {
  contentPaddingPx: number | string;
  contentPaddingBottomPx?: number | string;
  contentPaddingTopPx?: number | string;
}

export interface AlineDialogProps extends Omit<DialogProps, 'title'> {
  onClose: () => void;
  contentPaddingBottomPx?: number | string;
  contentPaddingPx?: number | string;
  contentPaddingTopPx?: number | string;
  // Set disableAltClose to hide x icon and disable escape and backdrop click.
  disableAltClose?: boolean;
  headerChildren?: React.ReactNode;
  headerClassName?: string;
  hideCloseIcon?: boolean;
  loading?: boolean;
  title?: React.ReactNode | string;
  // v9 removed the legacy `TransitionProps` slot from Dialog/Modal in favour
  // of `slotProps.transition`. AlineDialog keeps a `TransitionProps` prop in
  // its own public API (used by ~16 callers) and forwards it internally.
  TransitionProps?: Partial<MuiTransitionProps>;
  xDataCy?: string;
}

export function AlineDialog(props: AlineDialogProps): JSX.Element {
  const theme = useTheme();
  const {
    TransitionProps,
    children,
    classes,
    contentPaddingBottomPx,
    contentPaddingPx = theme.spacing(3),
    contentPaddingTopPx,
    disableAltClose = false,
    headerChildren,
    headerClassName,
    hideCloseIcon,
    loading = false,
    maxWidth = 'md',
    onClose,
    open,
    title,
    xDataCy = Select.dialog.alineDialog.closeButton,
    ...rest
  } = props;
  const { classes: internalClasses } = useStyles({
    contentPaddingBottomPx,
    contentPaddingPx,
    contentPaddingTopPx,
  });

  const renderTitle = () => {
    if (typeof title === 'string')
      return (
        <Typography className={internalClasses.title} variant="h1">
          {title}
        </Typography>
      );

    return title;
  };

  return (
    <Dialog
      classes={{ ...classes, paper: clsx(classes?.paper, internalClasses.dialogPaper) }}
      maxWidth={maxWidth}
      open={open}
      onClose={() => {
        // v9 dropped `disableEscapeKeyDown` from Dialog. Returning early from
        // onClose blocks both escape and backdrop close paths (MUI fires
        // onClose with a reason but doesn't auto-close; the parent's open
        // state controls visibility).
        if (disableAltClose) return;

        onClose();
      }}
      {...rest}
      slotProps={{
        ...rest.slotProps,
        transition: { ...rest.slotProps?.transition, ...TransitionProps },
      }}
    >
      <header className={clsx(headerClassName, internalClasses.header)}>
        {renderTitle()}
        {headerChildren}
        {!disableAltClose && !hideCloseIcon && (
          <SmallCloseIconButton
            className={internalClasses.closeButton}
            data-cy={xDataCy}
            disabled={loading}
            onClick={onClose}
          />
        )}
      </header>
      <div className={internalClasses.content}>{children}</div>
    </Dialog>
  );
}

export function AnimatedAlineDialog(props: AlineDialogProps): JSX.Element {
  const { children, ...rest } = props;
  const [setRef, measurements] = useAlineMeasure<HTMLDivElement>();
  const styles = useSpring({ height: measurements?.height ?? 0 });

  return (
    <AlineDialog {...rest}>
      <animated.div style={styles}>
        <div ref={setRef}>{children}</div>
      </animated.div>
    </AlineDialog>
  );
}
